// src/layouts/SectionTabs.tsx
//
// The destination's horizontal section bar (hybrid-3c.md §5.1), anchored to
// the app bar rather than living inside each page: the place where you switch
// section is the same pixel on every screen, it does not scroll away with the
// content, and no page has to remember to mount it. Each tab is a real link,
// so a section opens in a new browser tab like any other address.
import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { APP_BAR_HEIGHT, useSectionTabs } from './useSectionTabs';

export const SectionTabs: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { sections, tab, height } = useSectionTabs();

  if (sections.length === 0) return null;

  // A deep route (e.g. /reservations/:id) resolves to its section, so the
  // underline stays put on a detail page.
  const value = sections.some((section) => section.to === tab?.to) ? tab!.to : false;
  const tabHeight = `${height - 1}px`;

  return (
    <Box
      component="nav"
      aria-label={translate('shell.sections.label')}
      sx={{
        // Absolute inside the app bar, so the strip spans exactly the content
        // column and shifts with it when the rail collapses.
        position: 'absolute',
        top: `${APP_BAR_HEIGHT}px`,
        left: 0,
        right: 0,
        height: `${height}px`,
        backgroundColor: (theme.palette.background as { appbar?: string }).appbar || theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: isMobile ? '0 14px' : '0 22px',
        boxSizing: 'border-box',
      }}
    >
      <Tabs
        value={value}
        // Narrow screens scroll the row sideways instead of wrapping it.
        variant="scrollable"
        scrollButtons={false}
        sx={{
          minHeight: tabHeight,
          '& .MuiTabs-indicator': {
            height: '2px',
            backgroundColor: theme.palette.primary.main,
          },
          '& .MuiTab-root': {
            minHeight: tabHeight,
            minWidth: 'auto',
            padding: '0 14px',
            fontSize: '0.84375rem',
            fontWeight: 500,
            textTransform: 'none',
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.text.primary,
              fontWeight: 600,
            },
          },
        }}
      >
        {sections.map((section) => (
          <Tab
            key={section.to}
            value={section.to}
            label={translate(section.labelKey, { smart_count: 2 })}
            component={RouterLink}
            to={section.to}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default SectionTabs;
